// RecipientCard.jsx
// Rich profile card for a staged recipient — card layout preferred over chip/row.
// For internal Blitz users:
//   · Real profile photo fetched via useRecipientProfile (mirrors GlobalUserProfile)
//   · Gradient ring around avatar
//   · Name + @handle + Creator/User badge
//   · Clicking avatar or name → navigate to /@slug (GlobalUserProfile route)
//   · Skeleton shimmer while photo loads
// For email / phone invites:
//   · Generated initials avatar with colour-coded accent
//   · No profile link

import React, { useEffect, useState } from "react";
import {
  Box, Typography, Avatar, TextField, IconButton, Chip, Stack,
  InputAdornment, Tooltip, Skeleton,
} from "@mui/material";
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { fmtUSD } from "./T2a.utils";

// ── Accent config per kind ────────────────────────────────────────────────────
const KIND_STYLE = {
  internal: {
    label:    "Blitz user",
    colorKey: "primary",
    topBar:   (t) => `linear-gradient(90deg, ${t.palette.primary.main}, ${alpha(t.palette.primary.light, 0.5)})`,
    ring:     (t) => `linear-gradient(135deg, ${t.palette.primary.main}, ${alpha(t.palette.primary.light, 0.6)})`,
    Icon:     VerifiedIcon,
  },
  email: {
    label:    "Email invite",
    colorKey: "info",
    topBar:   (t) => `linear-gradient(90deg, ${t.palette.info.main}, ${alpha(t.palette.info.light, 0.5)})`,
    ring:     null,
    Icon:     EmailIcon,
  },
  phone: {
    label:    "SMS invite",
    colorKey: "success",
    topBar:   (t) => `linear-gradient(90deg, ${t.palette.success.main}, ${alpha(t.palette.success.light, 0.5)})`,
    ring:     null,
    Icon:     PhoneIcon,
  },
};

/**
 * Props
 * ─────
 * recipient      : Recipient (from useRecipients)
 * amount         : string   (raw field value)
 * onSetAmount    : (id, value) => void
 * onRemove       : (id) => void
 * autoFocus      : bool
 * profileData    : { pfphref, displayName, isCreator, loading } from useRecipientProfile.resolve()
 */
export function RecipientCard({
  recipient: r,
  amount,
  onSetAmount,
  onRemove,
  autoFocus,
  profileData,
}) {
  const theme    = useTheme();
  const navigate = useNavigate();
  const [focused, setFocused] = useState(false);

  const style    = KIND_STYLE[r.kind] || KIND_STYLE.internal;
  const colorKey = style.colorKey;
  const accent   = theme.palette[colorKey]?.main || theme.palette.primary.main;

  const parsedAmt  = parseFloat(amount) || 0;
  const hasAmount  = parsedAmt > 0;

  // Resolved profile (live fetch for internal, fallback to search data)
  const photo       = profileData?.pfphref     ?? r.pfphref ?? null;
  const displayName = profileData?.displayName ?? r.label   ?? "";
  const isCreator   = profileData?.isCreator   ?? !!r.creatorUsername;
  const photoLoading = r.kind === "internal" && (profileData?.loading ?? false);

  // Profile slug for /@username routing (GlobalUserProfile)
  const profileSlug =
    r.kind === "internal"
      ? r.creatorUsername || r.username || String(r.globalUserId || "")
      : null;

  const goToProfile = () => profileSlug && navigate(`/profile/${profileSlug}`);

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: focused
          ? accent
          : hasAmount
          ? alpha(accent, 0.35)
          : theme.palette.divider,
        bgcolor: focused ? alpha(accent, 0.025) : "background.paper",
        overflow: "hidden",
        transition: "all 0.2s ease",
        boxShadow: focused
          ? `0 0 0 3px ${alpha(accent, 0.12)}, 0 4px 16px ${alpha(accent, 0.07)}`
          : hasAmount
          ? `0 2px 10px ${alpha(accent, 0.08)}`
          : "none",
        "&:hover": {
          borderColor: alpha(accent, 0.5),
          boxShadow: `0 4px 18px ${alpha(accent, 0.12)}`,
          transform: "translateY(-1px)",
        },
      }}
    >
      {/* Coloured accent bar */}
      <Box sx={{ height: 3, background: style.topBar(theme) }} />

      {/* ── Header ── */}
      <Box sx={{ px: 2, pt: 2, pb: 1.25, display: "flex", alignItems: "flex-start", gap: 1.5 }}>

        {/* Avatar */}
        <Tooltip
          title={profileSlug ? `View @${profileSlug}` : ""}
          placement="top"
          disableHoverListener={!profileSlug}
        >
          <Box
            onClick={profileSlug ? goToProfile : undefined}
            sx={{
              position: "relative",
              flexShrink: 0,
              cursor: profileSlug ? "pointer" : "default",
              "&:hover .pov": profileSlug ? { opacity: 1 } : {},
            }}
          >
            {/* Gradient ring — internal only */}
            {r.kind === "internal" && style.ring && (
              <Box
                sx={{
                  position: "absolute",
                  inset: -2.5,
                  borderRadius: "50%",
                  background: style.ring(theme),
                  zIndex: 0,
                }}
              />
            )}

            {/* Avatar or skeleton */}
            {photoLoading ? (
              <Skeleton
                variant="circular"
                width={50}
                height={50}
                sx={{ position: "relative", zIndex: 1 }}
              />
            ) : (
              <Avatar
                src={photo || undefined}
                sx={{
                  width: 50,
                  height: 50,
                  bgcolor: alpha(accent, 0.12),
                  color: accent,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  position: "relative",
                  zIndex: 1,
                  border: r.kind === "internal" ? "2.5px solid" : "none",
                  borderColor: "background.paper",
                }}
              >
                {!photo && (displayName || "?")[0].toUpperCase()}
              </Avatar>
            )}

            {/* Hover overlay */}
            {profileSlug && (
              <Box
                className="pov"
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  bgcolor: alpha("#000", 0.42),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0,
                  transition: "opacity 0.15s",
                  zIndex: 2,
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 16, color: "white" }} />
              </Box>
            )}
          </Box>
        </Tooltip>

        {/* Name + badges + contact */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Name row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              mb: 0.35,
              cursor: profileSlug ? "pointer" : "default",
              "&:hover .nm": profileSlug ? { color: accent } : {},
            }}
            onClick={profileSlug ? goToProfile : undefined}
          >
            {photoLoading ? (
              <Skeleton width={120} height={16} sx={{ borderRadius: 1 }} />
            ) : (
              <Typography
                className="nm"
                variant="body2"
                sx={{ fontWeight: 700, fontSize: "0.92rem", lineHeight: 1.3, transition: "color 0.15s" }}
                noWrap
              >
                {displayName}
              </Typography>
            )}
            {r.kind === "internal" && !photoLoading && (
              <CheckCircleIcon sx={{ fontSize: 14, color: accent, flexShrink: 0 }} />
            )}
          </Box>

          {/* Badge row */}
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.4, flexWrap: "wrap" }}>
            {r.kind === "internal" && !photoLoading && (
              <>
                <Chip
                  label={isCreator ? "Creator" : "User"}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    bgcolor: isCreator
                      ? alpha(theme.palette.secondary.main, 0.1)
                      : alpha(accent, 0.1),
                    color: isCreator ? "secondary.main" : accent,
                    border: `1px solid ${alpha(isCreator ? theme.palette.secondary.main : accent, 0.2)}`,
                    "& .MuiChip-label": { px: 0.7 },
                  }}
                />
              </>
            )}
            {r.kind !== "internal" && (
              <Chip
                label={style.label}
                size="small"
                icon={<style.Icon sx={{ fontSize: "11px !important", color: `${accent} !important` }} />}
                sx={{
                  height: 18, fontSize: "0.6rem", fontWeight: 700,
                  bgcolor: alpha(accent, 0.1), color: accent,
                  border: `1px solid ${alpha(accent, 0.2)}`,
                  "& .MuiChip-label": { px: 0.7 },
                }}
              />
            )}
          </Stack>

          {/* Sub-label: @handle or contact */}
          {r.kind === "internal" && r.username && !photoLoading && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem" }} noWrap>
              @{r.username}
            </Typography>
          )}
          {r.kind === "email" && r.email && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem" }} noWrap>
              {r.email}
            </Typography>
          )}
          {r.kind === "phone" && r.phoneNumber && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem" }} noWrap>
              {r.phoneNumber}
            </Typography>
          )}
        </Box>

        {/* Remove */}
        <IconButton
          size="small"
          onClick={() => onRemove(r.id)}
          sx={{
            color: "text.disabled",
            p: 0.5,
            mt: -0.5,
            mr: -0.5,
            "&:hover": { color: "error.main", bgcolor: alpha(theme.palette.error.main, 0.08) },
            transition: "all 0.15s",
          }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* ── Amount field ── */}
      <Box sx={{ px: 2, pb: 2 }}>
        <TextField
          type="number"
          value={amount}
          onChange={(e) => onSetAmount(r.id, e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="0.00"
          autoFocus={autoFocus}
          fullWidth
          size="small"
          inputProps={{ min: 0.01, step: 0.01 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 800,
                    color: hasAmount ? accent : "text.disabled",
                    fontSize: "1.15rem",
                    lineHeight: 1,
                    transition: "color 0.15s",
                  }}
                >
                  $
                </Typography>
              </InputAdornment>
            ),
            sx: {
              fontSize: "1.35rem",
              fontWeight: 700,
              borderRadius: 2,
              letterSpacing: "-0.01em",
              bgcolor: alpha(accent, hasAmount ? 0.04 : 0),
              transition: "background 0.2s",
              "& input": { py: 1.1 },
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: hasAmount ? alpha(accent, 0.4) : undefined, transition: "border-color 0.15s" },
              "&:hover fieldset": { borderColor: accent },
              "&.Mui-focused fieldset": { borderColor: accent, borderWidth: 2 },
            },
          }}
        />

        
      </Box>
    </Box>
  );
}